// useViewportBreakpoint — single source of truth for the Tailwind responsive
// breakpoints used for JS-gated layout branching (RFC-0014 Phase 0 Task 0.1,
// created during Phase 1; extended in Phase 3 Task 3.1 with `isBelowLg`).
//
// Exposes:
//   - `isBelowMd` — true below Tailwind `md` (768px): the app-shell drawer↔sidebar
//     fork (Phase 1).
//   - `isBelowLg` — true below Tailwind `lg` (1024px): the doc-reader "Contents"
//     drawer fork (Phase 3). The reader's 3-column grid collapses at `lg`, not
//     `md` (audit matrix / Open-Question #1 resolution), so its nav drawer is
//     scoped to the reader's actual multi-column point.
//
// Mirrors the matchMedia lifecycle in `context/theme.tsx`: lazy initial read from
// `matches`, modern `addEventListener('change')` with a legacy `addListener`
// fallback, and symmetric cleanup on unmount. Access to `window.matchMedia` is
// guarded so the hook is SSR / no-`matchMedia` safe, defaulting to the widest
// band (`{ isBelowMd: false, isBelowLg: false }` — treat as desktop).

import { useEffect, useState } from 'react';

// Tailwind's `md` / `lg` breakpoints. `isBelow*` is true when the viewport is
// narrower than the breakpoint (the query does NOT match).
const MD_QUERY = '(min-width: 768px)';
const LG_QUERY = '(min-width: 1024px)';

function readMatches(query: string): boolean {
  try {
    return window.matchMedia(query).matches;
  } catch {
    return true; // SSR / no `window` / no `matchMedia` → treat as widest (desktop).
  }
}

/**
 * Subscribe to a media query's `change` events with the modern/legacy fallback,
 * returning a cleanup that removes the listener. No-op (and safe) when
 * `matchMedia` is unavailable.
 */
function subscribe(query: string, onChange: (matches: boolean) => void): () => void {
  let mql: MediaQueryList;
  try {
    mql = window.matchMedia(query);
  } catch {
    return () => {};
  }

  const handleChange = (event: MediaQueryListEvent) => onChange(event.matches);

  // Modern browsers: addEventListener. Legacy (Safari < 14): addListener.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', handleChange);
  } else if (typeof mql.addListener === 'function') {
    mql.addListener(handleChange);
  }

  return () => {
    if (typeof mql.removeEventListener === 'function') {
      mql.removeEventListener('change', handleChange);
    } else if (typeof mql.removeListener === 'function') {
      mql.removeListener(handleChange);
    }
  };
}

export function useViewportBreakpoint(): { isBelowMd: boolean; isBelowLg: boolean } {
  const [isBelowMd, setIsBelowMd] = useState(() => !readMatches(MD_QUERY));
  const [isBelowLg, setIsBelowLg] = useState(() => !readMatches(LG_QUERY));

  useEffect(() => subscribe(MD_QUERY, (matches) => setIsBelowMd(!matches)), []);
  useEffect(() => subscribe(LG_QUERY, (matches) => setIsBelowLg(!matches)), []);

  return { isBelowMd, isBelowLg };
}
